/**
 * Couche d'accès à l'API SmartMobility (backend Django / DRF).
 *
 * Chaque fonction tente l'appel réseau ; si le backend est injoignable,
 * elle renvoie des données de démonstration (mêmes structures que le mock
 * backend) afin que le dashboard reste pleinement présentable hors ligne.
 *
 * L'URL de base est configurable via la variable d'environnement
 * REACT_APP_API_URL (ex. dans un fichier .env.local).
 */
import axios from 'axios';
import * as extra from './mockExtra';

const BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const client = axios.create({
  baseURL: BASE_URL,
  timeout: 12000,
  headers: { 'Content-Type': 'application/json' },
});

export const TOKEN_KEY = 'sm_token';
export const USER_KEY = 'sm_user';

// Attache automatiquement le token d'authentification s'il existe.
client.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Token ${t}`;
  return cfg;
});

// Sur 401 (token invalide/expiré) : purge la session et renvoie au login.
client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response && err.response.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      if (!window.location.pathname.startsWith('/login')) {
        window.location.assign('/login');
      }
    }
    return Promise.reject(err);
  },
);

/* ------------------------------------------------------------------ */
/* Données de démonstration (repli quand l'API est hors ligne)         */
/* ------------------------------------------------------------------ */

const ZONES = [
  { id: 'DKR-01', nom: 'Dakar-Plateau', lat: 14.668, lon: -17.436 },
  { id: 'DKR-02', nom: 'Grand Dakar', lat: 14.71, lon: -17.45 },
  { id: 'DKR-03', nom: 'Parcelles Assainies', lat: 14.76, lon: -17.425 },
  { id: 'PIK-01', nom: 'Pikine', lat: 14.755, lon: -17.39 },
  { id: 'GUE-01', nom: 'Guédiawaye', lat: 14.77, lon: -17.405 },
  { id: 'RUF-01', nom: 'Rufisque', lat: 14.715, lon: -17.267 },
];

const MOCK = {
  health: { status: 'ok', service: 'SmartMobility API', version: '0.1.0' },
  odFlows: {
    zones: ZONES,
    flows: [
      { origin: 'DKR-01', destination: 'PIK-01', volume: 12450 },
      { origin: 'PIK-01', destination: 'DKR-01', volume: 11820 },
      { origin: 'DKR-02', destination: 'DKR-01', volume: 9640 },
      { origin: 'GUE-01', destination: 'DKR-01', volume: 8730 },
      { origin: 'DKR-03', destination: 'PIK-01', volume: 4380 },
      { origin: 'RUF-01', destination: 'DKR-01', volume: 5210 },
    ],
    unit: 'déplacements/jour',
    source: 'demo',
  },
  deserts: {
    deserts: [
      { zone_id: 'RUF-01', nom: 'Rufisque', score: 0.78, niveau: 'élevé', population_estimee: 195000 },
      { zone_id: 'DKR-03', nom: 'Parcelles Assainies', score: 0.61, niveau: 'moyen', population_estimee: 320000 },
      { zone_id: 'GUE-01', nom: 'Guédiawaye', score: 0.54, niveau: 'moyen', population_estimee: 330000 },
    ],
    echelle: '0 (bien desservi) → 1 (désert total)',
    source: 'demo',
  },
  predictions: {
    zone_id: 'DKR-01',
    horizon_heures: 24,
    predictions: [
      120, 90, 60, 45, 70, 180, 520, 980, 1240, 1100, 980, 1020, 1150, 1080,
      990, 1010, 1180, 1390, 1280, 860, 540, 380, 260, 170,
    ].map((v, h) => ({ heure: `${String(h).padStart(2, '0')}:00`, flux_prevu: v })),
    modele: 'profil horaire moyen',
    source: 'demo',
  },
  anomalies: {
    anomalies: [
      {
        id: 'ANO-1042', zone_id: 'DKR-01', type: 'pic_affluence', gravite: 'haute',
        horodatage: '2026-06-03T08:15:00+00:00',
        description: 'Affluence 3× supérieure à la normale (Plateau).',
      },
      {
        id: 'ANO-1043', zone_id: 'PIK-01', type: 'chute_trafic', gravite: 'moyenne',
        horodatage: '2026-06-03T09:40:00+00:00',
        description: 'Chute brutale du trafic, incident probable.',
      },
    ],
    source: 'demo',
  },
  crowding: {
    stops: ZONES.map((z, i) => ({
      zone_id: z.id,
      nom: z.nom,
      niveau: ['faible', 'modéré', 'élevé', 'modéré', 'faible', 'élevé'][i % 6],
      taux_occupation: [0.25, 0.55, 0.88, 0.5, 0.2, 0.91][i % 6],
    })),
    source: 'demo',
  },
};

/**
 * Exécute une requête en repliant sur les données de démo en cas d'échec.
 * @returns {Promise<{data: any, live: boolean}>}
 */
async function withFallback(promise, fallback) {
  try {
    const { data } = await promise;
    return { data, live: true };
  } catch (err) {
    return { data: fallback, live: false };
  }
}

export const api = {
  // --- Authentification (lèvent une erreur en cas d'échec) ---
  login: (username, password) => client.post('/auth/login/', { username, password }),
  logout: () => client.post('/auth/logout/'),
  me: () => client.get('/auth/me/'),

  health: () => withFallback(client.get('/health/'), MOCK.health),
  odFlows: () => withFallback(client.get('/flows/od/'), MOCK.odFlows),
  mobilityDeserts: () => withFallback(client.get('/mobility-deserts/'), MOCK.deserts),
  predictions: (zoneId) =>
    withFallback(
      client.get('/predictions/', { params: zoneId ? { zone_id: zoneId } : {} }),
      MOCK.predictions,
    ),
  activityMap: () => withFallback(client.get('/activity-map/'), { zones: [], source: 'demo' }),
  forecast: () => withFallback(client.get('/forecast/'), { series: [], source: 'demo' }),
  anomalies: () => withFallback(client.get('/anomalies/'), MOCK.anomalies),
  crowding: () => withFallback(client.get('/crowding/'), MOCK.crowding),
  route: (origin, destination) =>
    withFallback(client.post('/route/', { origin, destination }), null),

  // --- Aide à la décision (servie par l'API, repli local si hors ligne) ---
  profiling: () => withFallback(client.get('/profiling/'), extra.profiling),
  accessibility: () => withFallback(client.get('/accessibility/'), extra.accessibility),
  events: () => withFallback(client.get('/events/'), extra.events),
  eventForecast: () => withFallback(client.get('/event-forecast/'), extra.eventForecast),
  optimizer: () => withFallback(client.get('/optimizer/'), extra.optimizer),
  openData: () => withFallback(client.get('/open-data/'), extra.openData),
  simulate: extra.simulate,
};

export { ZONES };
